import React from 'react';
import { useRouter } from 'next/router';
import imageUrlBuilder from '@sanity/image-url';

import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import {
  ArrowForward, Star, GradientWand,
} from '@tmap/mmm-style-guide/src/Icon';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';

import ColorBlocks from '../../layouts/colorBlocks';
import IncentiveCard from '../incentiveCard';
import ActiveOffersBlock from '../homepage/activeOffersBlock';
import client from '../../client';
import useCurrentUser from '../../hooks/useCurrentUser';

const LOADING_IMAGE_URL = 'https://cdn.sanity.io/images/u721u3r1/production/ad02846f149f28c0b54cac9df1b61928d738880e-83x83.gif';
const builder = imageUrlBuilder(client);

export const GradientText = styled(Typography)(() => ({
  background: 'linear-gradient(90deg, #005D46 0.3%, #A07FA3 72.18%)',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  display: 'inline-flex',
}));

const PreferencesButton = styled(Button)(({ theme }) => ({
  '& .MuiButton-root': {
    padding: theme.spacing(0.75, 5.75),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
  },
}));

const PreferencesDescriptionCard = styled(Card)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

function NoPreferencesBlock() {
  const router = useRouter();
  return (
    <ColorBlocks.Block color='dim' spacing='medium' sx={{ boxShadow: '0px 0px 10.6px 0px rgba(0, 0, 0, 0.07) inset' }}>
      <Grid container columnSpacing={7} rowSpacing={2} sx={{ py: { xs: 1, md: 3 } }}>
        <Grid item xs={12}>
          <GradientText variant='subtitle2'>
            <GradientWand colors={['primary.light', 'pink.dark']} sx={{ mr: 0.5 }} /> Start here to see your best matches
          </GradientText>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography fontSize='20px' fontWeight={500} sx={{ mb: 4 }}>This page is powered by your preferences. Answer just a few, short questions, and we’ll build your personalized list of recs.</Typography>
          <PreferencesButton
            variant='contained'
            size='medium'
            color='inverted'
            href={`/questions/module/eligibility?returnTo=${router.asPath}&completeAll=true`}
            disableElevation
          >Tell Us About You <ArrowForward sx={{ ml: 1 }} />
          </PreferencesButton>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container spacing={1}>
            {[
              'Discover places that match your vibe.',
              'Recommended programs that suit your personal interests',
              'Tailored results to save you time.',
            ].map((text, i) => (
              <Grid item xs={12} key={`preference-benefits-card-${i}`}>
                <PreferencesDescriptionCard elevation={0}>
                  <Star sx={{ mr: 1, fontSize: 'small', color: (theme) => theme.palette.pink.dark }} />
                  <Typography variant='caption'>{text}</Typography>
                </PreferencesDescriptionCard>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </ColorBlocks.Block>
  );
}

function EmptyIncentiveResults(props) {
  const { user } = props;

  const router = useRouter();
  if (!user?.preferences) {
    return (<NoPreferencesBlock />);
  }

  return (
    <ColorBlocks.Block color='dim' spacing='large' sx={{ boxShadow: '0px 0px 10.6px 0px rgba(0, 0, 0, 0.07) inset' }}>
      <Grid container rowSpacing={2} columnSpacing={1} justifyContent='center' sx={{ textAlign: 'center' }}>
        <Grid item xs={12}>
          <Typography fontSize='20px' fontWeight={500}>
            We didn’t find a perfect match — <span style={{ fontStyle: 'italic' }}>yet</span>.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography color='textSecondary' variant='body1'>
            Your preferences help guide us — and new programs are added regularly. In the
            meantime, browse everything we offer and we’ll let you know when new opportunities
            pop up.
          </Typography>
        </Grid>
        <Grid item xs={12} md='auto' sx={{ mt: 3 }}>
          <Button
            variant='contained'
            size='medium'
            color='inverted'
            href='/get-paid/more'
            disableElevation
            sx={{ width: '100%' }}
          >
            Back to All Programs
          </Button>
        </Grid>
        <Grid item xs={12} md='auto' sx={{ mt: { xs: 0, md: 3 } }}>
          <Button
            variant='outlined'
            size='medium'
            color='inverted'
            href={`/questions/module/eligibility?completeAll=true&returnTo=${router.asPath}`}
            disableElevation
            sx={{ width: '100%' }}
          >
            Review My Preferences
          </Button>
        </Grid>
      </Grid>
    </ColorBlocks.Block>
  );
}

function RecommendedProgramsList({
  incentives, isLoading,
}) {
  const user = useCurrentUser();
  return (
    <>
      {(isLoading && user?._id) && (
        <ColorBlocks.Block color='none' spacing='large'>
          <Grid container spacing={2} justifyContent='center' sx={{ mb: 9 }}>
            <Grid item>
              <img alt='' src={builder.image(LOADING_IMAGE_URL).url()} />
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Typography fontSize='20px' fontWeight={500}>
                Building your list of <br />
                personalized recommendations...
              </Typography>
            </Grid>
          </Grid>
        </ColorBlocks.Block>
      )}
      {(!isLoading && incentives?.length > 0) && (
        <ColorBlocks.Block color='none' spacing='large'>
          <Box sx={{ mb: 4 }}>
            <Typography
              component='span'
              fontSize='28px'
              fontWeight={700}
            >
              Programs&nbsp;
            </Typography>
            <GradientText
              component='span'
              fontSize='28px'
              fontWeight={700}
            >For You
            </GradientText>
            <Typography variant='body1'>
              See the programs we recommend for you based on your shared interests and
              needs.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {incentives.map((incentive, i) => {
              if (!incentive) {
                return null;
              }
              return (
                <Grid item xs={12} sm={6} md={4} key={`incCard-${incentive._id}`}>
                  <IncentiveCard
                    incentive={incentive}
                    shouldLazyLoad={i > 3}
                    lazyProps={{ offset: 1000 }}
                    placementId={`recommendedOffers-${i}`}
                    pageId='recommendedOffers'
                  />
                </Grid>
              );
            })}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Typography variant='body2' color='text.disabled' sx={{ mt: { xs: 1, md: 3 }, mb: 2 }}>
                You’ve reached the end of the For You list. Keep browsing to find the right fit.
              </Typography>
              <Button variant='outlined' color='primary' href='/get-paid/more?el=true'>
                View All Programs
              </Button>
            </Grid>
          </Grid>
        </ColorBlocks.Block>
      )}
      {
        (!user?._id || (!isLoading && incentives?.length === 0)) && (
          <EmptyIncentiveResults user={user} />
        )
      }
      {
        user?._id && (
          <ActiveOffersBlock
            alternateHeader={user?.firstName ? `${user.firstName}, pick up where you left off` : 'Pick up where you left off'}
            alternateSubHeader='Ready, Set, Go: Take another peek at Programs you’ve recently visited'
            headerStyle={{ fontSize: { xs: '20px', md: '24px' } }}
            blockProps={{ spacing: 'large' }}
          />
        )
      }
    </>
  );
}

export default RecommendedProgramsList;
